/**
 * Real or Paper: the two account modes of the Wealth section, and the names
 * both sides of the wire agree on. No imports, so the browser, the API and the
 * check suites can all read it.
 *
 * Paper is a demonstration mode. It shows the same list of accounts with data
 * that is made up from nothing (lib/wealth/paperData.ts): no figure in it is
 * derived from a real one. The browser keeps the choice in one per-device key
 * and says it on every request to the account routes with one header; the API
 * turns that header into a request-scoped mode (lib/wealth/paperMode.ts), and
 * the Wealth store and the journal read the mode at one seam each.
 */

export type AccountMode = "real" | "paper";

/** The request header the browser sends while Paper is on. Its absence is Real. */
export const ACCOUNT_MODE_HEADER = "x-mpp-account-mode";

/**
 * The choice, per device. Not synced (lib/safeLocalStorage.ts lists it with
 * the per-machine keys): switching to Paper on the machine a demonstration
 * runs on must not switch the owner's other machines, and a reload in the
 * middle of a demonstration must come back in Paper.
 */
export const ACCOUNT_MODE_KEY = "mpp:wealth:account-mode:v1";

/**
 * The API paths that carry account data: the header goes on these.
 * `/api/options/recommend` is here for its journal overlay (which names are
 * already open, held shares, a size from the journal's equity).
 */
export const ACCOUNT_API_PREFIXES = ["/api/wealth", "/api/trades", "/api/ibkr", "/api/options/recommend"] as const;

export function isAccountApiPath(pathname: string): boolean {
  return ACCOUNT_API_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`) || pathname.startsWith(`${p}?`));
}

/**
 * Routes that are a broker's own account and nothing else: the IBKR gateway's
 * positions, orders, fills and session. Paper has no copy of them and must not
 * show them, so a Paper request to one is refused before its handler runs
 * (lib/http/dispatchApi.ts).
 */
export const PAPER_REFUSED_PREFIXES = ["/api/ibkr"] as const;

export function isPaperRefusedPath(pathname: string): boolean {
  return PAPER_REFUSED_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

/**
 * Browser keys of the Wealth section that hold no account data: how a page is
 * laid out and what is shown. Paper and Real share them, so a demonstration
 * looks like the owner's own screen. Every OTHER `mpp:wealth*` / `mpp:trades*`
 * key is read and written under its paper twin while Paper is on
 * (`paperStorageKey`), so a new key is separate by default and has to be
 * listed here to be shared.
 */
const SHARED_EXACT = new Set<string>([
  ACCOUNT_MODE_KEY,
  "mpp:wealth-hide-amounts:v1",
  "mpp:wealth-holding-cols:v1",
  "mpp:wealth-holding-cols:v2",
  "mpp:wealth-book-pane-seen:v1",
  "mpp:trades:tab:v1",
]);

/** A panel workspace's saved arrangement (`mpp:wealth-overview:pw:v2`). */
const SHARED_PATTERN = /^mpp:wealth-[a-z-]+:pw:v\d+$/;

const PAPER_PREFIX = "mpp:paper:";

/** Whether `key` is one Paper keeps a separate copy of. */
export function isAccountDataKey(key: string): boolean {
  if (!(key.startsWith("mpp:wealth") || key.startsWith("mpp:trades"))) return false;
  if (SHARED_EXACT.has(key) || SHARED_PATTERN.test(key)) return false;
  return true;
}

/**
 * The key Paper mode reads and writes in place of `key`:
 * `mpp:wealth-forecast:scenarios:v1` becomes
 * `mpp:paper:wealth-forecast:scenarios:v1`. The version suffix is the
 * original's, so a bump of the real key bumps its twin with it. A key that
 * holds no account data comes back unchanged.
 */
export function paperStorageKey(key: string): string {
  return isAccountDataKey(key) ? `${PAPER_PREFIX}${key.slice("mpp:".length)}` : key;
}

/** One sentence for a control that is switched off in Paper mode. */
export const PAPER_OFF_REASON =
  "Paper account: this is switched off, because paper data is made up and nothing in Paper mode may contact a bank, a broker or a billed provider. Switch to Real to use it.";

/** The marker every Wealth page shows while Paper is on. */
export const PAPER_MARKER = "Paper account: every balance, holding, transaction and trade on this page is made up.";
